import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Zap, 
  MessageSquare, 
  BarChart3,
  TrendingUp,
  Settings,
  Target,
  Clock
} from 'lucide-react';
import { userDetailService, UserAIUsage } from '@/services/admin/user-detail-service';
import { toast } from 'sonner';

interface UserAIFeaturesTabProps {
  userId: string;
}

export const UserAIFeaturesTab: React.FC<UserAIFeaturesTabProps> = ({ userId }) => {
  const [aiUsage, setAiUsage] = useState<UserAIUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const usageData = await userDetailService.getUserAIUsage(userId);
      setAiUsage(usageData);
    } catch (error) {
      console.error('Error fetching AI usage data:', error);
      toast.error('Failed to load AI usage data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const getUsagePercentage = (used: number, limit: number) => {
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-500';
    if (percentage >= 70) return 'text-yellow-500';
    return 'text-green-500';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!aiUsage) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Zap className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
        <p>Failed to load AI usage data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AI Usage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Messages</p>
                <p className="text-2xl font-bold">{aiUsage.totalMessages.toLocaleString()}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Analysis Sessions</p>
                <p className="text-2xl font-bold">{aiUsage.totalAnalysisSessions.toLocaleString()}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Topics</p>
                <p className="text-2xl font-bold">{aiUsage.topicUsage.length}</p>
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Topic Usage & Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Usage by Topic & Limits
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aiUsage.topicUsage.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No topic usage data available</p>
            </div>
          ) : (
            <div className="space-y-6">
              {aiUsage.topicUsage.map((topic) => {
                const dailyPercentage = getUsagePercentage(topic.messageCount, topic.dailyLimit);
                const monthlyPercentage = getUsagePercentage(topic.messageCount, topic.monthlyLimit);
                
                return (
                  <div key={topic.topic} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium capitalize">{topic.topic.replace('_', ' ')}</h3>
                      <Badge variant="outline">
                        {topic.messageCount} messages
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Daily Usage</span>
                          <span className={getUsageColor(dailyPercentage)}>
                            {topic.messageCount} / {topic.dailyLimit}
                          </span>
                        </div>
                        <Progress value={dailyPercentage} className="h-2" />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Monthly Usage</span>
                          <span className={getUsageColor(monthlyPercentage)}>
                            {topic.messageCount} / {topic.monthlyLimit}
                          </span>
                        </div>
                        <Progress value={monthlyPercentage} className="h-2" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Usage Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Recent Usage Trend (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aiUsage.recentUsage.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No recent usage data available</p>
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={aiUsage.recentUsage}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    formatter={(value, name) => [value, name === 'messageCount' ? 'Messages' : 'Analysis']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="messageCount" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    name="Messages"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="analysisCount" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    name="Analysis"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Usage Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <MessageSquare className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">{aiUsage.totalMessages}</div>
              <div className="text-sm text-blue-600">Total Messages</div>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <BarChart3 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">{aiUsage.totalAnalysisSessions}</div>
              <div className="text-sm text-green-600">Analysis Sessions</div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Target className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-600">{aiUsage.topicUsage.length}</div>
              <div className="text-sm text-purple-600">Active Topics</div>
            </div>
            
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <TrendingUp className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-600">
                {aiUsage.recentUsage.reduce((sum, day) => sum + day.messageCount, 0)}
              </div>
              <div className="text-sm text-orange-600">7-Day Messages</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};